/**
 * SquiggleProvider
 * 
 * Main provider component for the Squiggle mascot system.
 * Initializes engines, director, and renders the SquiggleLayer.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { useSquiggleStore } from '../../stores/squiggleStore';
import { RiveController } from './engines/RiveController';
import { TrailEngine } from './engines/TrailEngine';
import { TargetManager } from './engines/TargetManager';
import { FlowDirector, FlowRegistry } from './director';
import { SquiggleFlow, MoodType, GestureType, Position } from './types';

// Import default flows
import { welcomeFlow } from './flows/welcome.flow';
import { createTaskFlow } from './flows/createTask.flow';
import { createRoutineFlow } from './flows/createRoutine.flow';

// ============================================
// Context Types
// ============================================

export interface SquiggleContextValue {
  // State access (from Zustand store)
  enabled: boolean;
  visible: boolean;
  mood: MoodType;
  position: Position;
  
  // Engine access
  riveController: RiveController;
  trailEngine: TrailEngine;
  targetManager: TargetManager;
  
  // Director access
  director: FlowDirector;
  
  // Actions
  dispatch: (event: string, payload?: unknown) => void;
  startFlow: (flowId: string) => void;
  stopFlow: () => void;
  
  // Direct controls
  setMood: (mood: MoodType) => void;
  trigger: (gesture: GestureType) => void;
  show: () => void;
  hide: () => void;
  moveTo: (position: Position) => void;
  
  // Settings
  setEnabled: (enabled: boolean) => void;
}

const SquiggleContext = createContext<SquiggleContextValue | null>(null);

// ============================================
// Provider Props
// ============================================

interface SquiggleProviderProps {
  children: React.ReactNode;
  flows?: SquiggleFlow[];
  rivePath?: string;
}

// ============================================
// Provider Component
// ============================================

export const SquiggleProvider: React.FC<SquiggleProviderProps> = ({
  children,
  flows = [],
  rivePath = '/assets/squiggle.riv',
}) => {
  const location = useLocation();
  const prefersReducedMotion = useReducedMotion();
  
  // Store state (selectors for values that affect rendering)
  const enabled = useSquiggleStore((state) => state.enabled);
  const visible = useSquiggleStore((state) => state.visible);
  const mood = useSquiggleStore((state) => state.mood);
  const position = useSquiggleStore((state) => state.position);
  
  // Store actions (stable, don't change on state updates)
  const storeShow = useSquiggleStore((state) => state.show);
  const storeHide = useSquiggleStore((state) => state.hide);
  const storeSetMood = useSquiggleStore((state) => state.setMood);
  const storeSetPosition = useSquiggleStore((state) => state.setPosition);
  const storeSetEnabled = useSquiggleStore((state) => state.setEnabled);
  
  // Initialize engines (lazy singleton pattern)
  const [riveController] = useState(() => new RiveController(rivePath));
  const [trailEngine] = useState(() => new TrailEngine());
  const [targetManager] = useState(() => new TargetManager());
  const [flowRegistry] = useState(() => {
    const registry = new FlowRegistry();
    // Register default flows
    registry.registerAll([welcomeFlow, createTaskFlow, createRoutineFlow]);
    // Register custom flows
    registry.registerAll(flows);
    return registry;
  });
  const [director] = useState(() => 
    new FlowDirector(riveController, trailEngine, targetManager, flowRegistry)
  );

  // Handle route changes
  useEffect(() => {
    director.handleRouteChange(location.pathname);
  }, [location.pathname, director]);

  // Handle reduced motion preference
  useEffect(() => {
    if (prefersReducedMotion) {
      trailEngine.setMode('off');
    }
  }, [prefersReducedMotion, trailEngine]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      riveController.dispose();
      trailEngine.dispose();
      targetManager.dispose();
      director.dispose();
    };
  }, [riveController, trailEngine, targetManager, director]);

  // Bubble interaction handlers - directly call director methods
  const handleBubbleNext = useCallback(() => {
    director.triggerBubbleNext();
  }, [director]);

  const handleBubbleDismiss = useCallback(() => {
    director.triggerBubbleDismiss();
  }, [director]);

  // Context actions
  const dispatch = useCallback((event: string, payload?: unknown) => {
    director.dispatch(event, payload);
  }, [director]);

  const startFlow = useCallback((flowId: string) => {
    director.startFlow(flowId);
  }, [director]);

  const stopFlow = useCallback(() => {
    director.stopFlow('interrupt');
  }, [director]);

  const setMood = useCallback((newMood: MoodType) => {
    riveController.setMood(newMood);
    storeSetMood(newMood);
  }, [riveController, storeSetMood]);

  const trigger = useCallback((gesture: GestureType) => {
    riveController.trigger(gesture);
  }, [riveController]);

  const show = useCallback(() => {
    storeShow();
  }, [storeShow]);

  const hide = useCallback(() => {
    storeHide();
  }, [storeHide]);

  const moveTo = useCallback((newPosition: Position) => {
    storeSetPosition(newPosition);
  }, [storeSetPosition]);

  const setEnabled = useCallback((newEnabled: boolean) => {
    storeSetEnabled(newEnabled);
  }, [storeSetEnabled]);

  // Context value
  const contextValue = useMemo<SquiggleContextValue>(() => ({
    enabled,
    visible,
    mood,
    position,
    riveController,
    trailEngine,
    targetManager,
    director,
    dispatch,
    startFlow,
    stopFlow,
    setMood,
    trigger,
    show,
    hide,
    moveTo,
    setEnabled,
  }), [
    enabled,
    visible,
    mood,
    position,
    riveController,
    trailEngine,
    targetManager,
    director,
    dispatch,
    startFlow,
    stopFlow,
    setMood,
    trigger,
    show,
    hide,
    moveTo,
    setEnabled,
  ]);

  // Mascot rendering is frozen for the MVP. The provider stays in the tree so
  // existing context consumers keep working, but we do not mount SquiggleLayer
  // and the bubble handlers below are intentionally no-ops in this phase.
  void handleBubbleNext;
  void handleBubbleDismiss;

  return (
    <SquiggleContext.Provider value={contextValue}>
      {children}
    </SquiggleContext.Provider>
  );
};

// ============================================
// Hook
// ============================================

export function useSquiggleContext(): SquiggleContextValue {
  const context = useContext(SquiggleContext);
  if (!context) {
    throw new Error('useSquiggleContext must be used within a SquiggleProvider');
  }
  return context;
}

export default SquiggleProvider;
